/**
 * Расчёт сроков по требованию сообщить ИНН.
 *
 * Мягкое напоминание считается от даты регистрации клиента (15 дней), как и раньше.
 *
 * Жёсткая блокировка привязана к дедлайну: бэкенд может присылать его сам —
 * необязательными полями `tax_id_intro_deadline` (unix-время в секундах) и
 * `tax_id_intro_delay_days`. Если их нет, дедлайн считается от даты регистрации.
 */

import { addDays, differenceInDays } from 'date-fns';
import type { Customer } from '../api/customer/types';

/** Отсрочка по умолчанию, если бэкенд не присылает свою */
export const DEFAULT_TAX_ID_INTRO_DELAY_DAYS = 30;

/** Через сколько дней после регистрации начинаем мягко напоминать про ИНН */
const REMINDER_AFTER_DAYS = 15;

/** ИНН ещё не указан — сроки имеют смысл только в этом случае */
const isTaxIdMissing = (customer?: Customer | null): boolean =>
    !!customer && (!customer.inn || customer.inn.trim() === '');

/** Длина отсрочки в днях: значение с бэкенда, иначе значение по умолчанию */
export const getTaxIdIntroDelayDays = (customer?: Customer | null): number => {
    const delayDays = customer?.tax_id_intro_delay_days;

    return typeof delayDays === 'number' && Number.isFinite(delayDays) && delayDays >= 0
        ? delayDays
        : DEFAULT_TAX_ID_INTRO_DELAY_DAYS;
};

/**
 * Момент, после которого доступ к сервису блокируется до сообщения ИНН.
 *
 * Приоритет у `tax_id_intro_deadline`: бэкенд может отвязать срок от даты регистрации
 * (например, дать отсрочку от момента введения требования). Если поля нет — считаем от регистрации.
 *
 * @returns null, если ИНН уже указан или срок не из чего посчитать
 */
export const getTaxIdIntroDeadline = (customer?: Customer | null): Date | null => {
    if (!isTaxIdMissing(customer) || !customer) return null;

    const deadline = customer.tax_id_intro_deadline;
    if (typeof deadline === 'number' && Number.isFinite(deadline) && deadline > 0) {
        return new Date(deadline * 1000);
    }

    const registeredAt = new Date(customer.created_at);
    if (Number.isNaN(registeredAt.getTime())) return null;

    return addDays(registeredAt, getTaxIdIntroDelayDays(customer));
};

/** Пора ли мягко напомнить про ИНН (прошло 15 дней с регистрации) */
export const isTaxIdReminderDue = (customer?: Customer | null, now: Date = new Date()): boolean => {
    if (!isTaxIdMissing(customer) || !customer) return false;

    const registeredAt = new Date(customer.created_at);
    if (Number.isNaN(registeredAt.getTime())) return false;

    return differenceInDays(now, registeredAt) >= REMINDER_AFTER_DAYS;
};

/** Пора ли показать блокирующую модалку (отсрочка истекла) */
export const isTaxIdBlockingDue = (customer?: Customer | null, now: Date = new Date()): boolean => {
    const deadline = getTaxIdIntroDeadline(customer);

    return deadline !== null && now.getTime() >= deadline.getTime();
};
